import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { Heading } from '@chakra-ui/layout';
import { Box, Button, Checkbox, Code, Flex, IconButton, NumberInput, NumberInputField, Text } from '@chakra-ui/react';
import { intervalToDuration } from 'date-fns';
import { useResource } from 'datx-jsonapi-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useCopyToClipboard } from 'react-use';

import CodeIcon from '@/assets/icons/code-icon.svg';
import LinkOffIcon from '@/assets/icons/ic-link-off.svg';
import LinkIcon from '@/assets/icons/ic-link.svg';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { IToastType } from '@/enums/ToastType';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useToast } from '@/hooks/useToast';

export const EmbedSettings = () => {
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const [, copyToClipboard] = useCopyToClipboard();
  const { t: time } = useQueryParams<{ t: string }>({
    t: StringParam,
  });
  const { t } = useTranslation();
  const toast = useToast();
  const startTimeSeconds = formatTParam(time);

  const duration = intervalToDuration({ start: 0, end: startTimeSeconds * 1000 });

  const hours = duration.hours.toString().padStart(2, '0');
  const minutes = duration.minutes.toString().padStart(2, '0');
  const secondsFormatted = duration.seconds.toString().padStart(2, '0');

  const defaultTime = `${hours}:${minutes}:${secondsFormatted}`;
  const [startTime, setStartTime] = useState<string>(defaultTime);

  const [width, setWidth] = useState(480);
  const [height, setHeight] = useState(320);
  const [customDimensions, setCustomDimensions] = useState(false);
  const [linkedSize, setLinkedSize] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const secondsForEmbed = hhmmssToSeconds(startTime);
  const src = `${process.env.NEXT_PUBLIC_BASE_URL}embed/${lectureId}-${lecture.slug}?t=${secondsForEmbed}`;
  const dimensionAttrs = customDimensions
    ? ` width="${width || 480}" height="${height || 320}"`
    : ' width="100%" style="aspect-ratio: 16/9;border:0"';
  const iframeCode = `<iframe${dimensionAttrs} src="${src}" title="${lecture.title}"  allowfullscreen></iframe>`;

  function handleCopy() {
    copyToClipboard(iframeCode);
    toast({ status: IToastType.SUCCESS, title: 'Embedded code successfully copied!' });
  }

  return (
    <Box flex={1}>
      <Heading fontSize="2xl">{t('lectureEmbed.heading')}</Heading>
      <Text mt={5}>{t('lectureEmbed.shortDescription')}</Text>
      <Text mt={5}>
        <Text as="span" fontWeight="bold" mr={3}>
          {t('lectureEmbed.startTime')}
        </Text>
        <InputMask
          defaultValue={startTime}
          style={{
            background: 'none',
            outline: 'none',
            borderBottom: '1px solid gray',
            width: '70px',
            paddingBottom: '8px',
          }}
          aria-label={t('lectureEmbed.startTime')}
          onChange={(e) => setStartTime(e.target.value)}
          alwaysShowMask
          mask="99:99:99"
          maskChar="0"
        />
      </Text>
      <Checkbox
        colorScheme="violet"
        fontWeight="bold"
        mt={5}
        isChecked={customDimensions}
        onChange={(e) => setCustomDimensions(e.target.checked)}
      >
        {t('lectureEmbed.customDimensions')}
      </Checkbox>
      {customDimensions && (
        <Flex gap={4} alignItems="flex-end" mt={5}>
          <FormControl>
            <FormLabel>{t('lectureEmbed.width')}</FormLabel>
            <NumberInput
              onChange={(e) => {
                const newWidth = parseInt(e);

                setWidth(newWidth);
                if (linkedSize && aspectRatio) setHeight(Math.round(newWidth / aspectRatio));
              }}
              value={width}
              min={200}
              max={2000}
            >
              <NumberInputField
                _focus={{ borderColor: 'violet.600', borderWidth: '2px' }}
                boxShadow="none !important"
                border="1px solid white"
              />
            </NumberInput>
          </FormControl>
          <IconButton
            size="md"
            onClick={() => {
              if (!linkedSize) setAspectRatio(width / height);
              else setAspectRatio(null);
              setLinkedSize((l) => !l);
            }}
            borderRadius={6}
            variant="solid"
            aria-label={linkedSize ? 'Unlink dimensions' : 'Link dimensions'}
            color="white"
            icon={linkedSize ? <LinkIcon /> : <LinkOffIcon />}
          />
          <FormControl>
            <FormLabel>{t('lectureEmbed.height')}</FormLabel>
            <NumberInput
              onChange={(e) => {
                const newHeight = parseInt(e);

                setHeight(newHeight);
                if (linkedSize && aspectRatio) setWidth(Math.round(newHeight * aspectRatio));
              }}
              value={height}
              min={280}
              max={2000}
            >
              <NumberInputField
                _focus={{ borderColor: 'violet.600', borderWidth: '2px' }}
                borderColor="transparent"
                boxShadow="none !important"
                border="1px solid white"
              />
            </NumberInput>
          </FormControl>
        </Flex>
      )}
      <Code w="full" borderRadius={4} bg="gray.900" color="gray.200" p={4} mt={8}>
        {iframeCode}
      </Code>
      <Button size="lg" mt={8} onClick={handleCopy} rightIcon={<CodeIcon />}>
        {t('lectureEmbed.copyEmbedCode')}
      </Button>
    </Box>
  );
};

function formatTParam(t: string) {
  const isNumber = /^\d+$/.test(t);

  if (!isNumber) return 0;
  return parseInt(t);
}

function hhmmssToSeconds(timeString: string) {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);

  return hours * 3600 + minutes * 60 + seconds;
}
